import {Box, Paper, Typography, Chip, Divider} from '@mui/material'

interface DetailsProposalTabProps {
    record: any
}

const formatDuration = (value?: number) => {
    if (!value && value !== 0) return '-'
    const seconds = (value / 1000).toFixed(2)
    return `${seconds}s`
}

const formatNumber = (value?: number | string) => {
    if (value === null || value === undefined) return '-'
    const num = typeof value === 'number' ? value : Number(value)
    if (Number.isNaN(num)) return value?.toString() ?? '-'
    return num.toLocaleString()
}

function DetailsProposalTab({record}: DetailsProposalTabProps) {
    const meta = record?.data?.meta

    if (!meta) {
        return (
            <Typography variant="body2" color="text.secondary">
                No meta details available for this proposal.
            </Typography>
        )
    }

    const fnsListEntries = Object.entries(meta.fnsList ?? {}) as [string, number][]
    const proposalsCountsEntries = Object.entries(meta.proposalsCounts ?? {}) as [string, number][]
    const consoleErrors = meta.consoleErrors ?? []

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2}}>
                <Paper elevation={0} sx={{padding: 2, border: '1px solid #e0e0e0', borderRadius: 2}}>
                    <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                        Algo
                    </Typography>
                    <Typography variant="h6">{formatDuration(meta.totalRatingTime)}</Typography>
                </Paper>
                <Paper elevation={0} sx={{padding: 2, border: '1px solid #e0e0e0', borderRadius: 2}}>
                    <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                        S3
                    </Typography>
                    <Typography variant="h6">{formatDuration(meta.totalDownloadTime)}</Typography>
                </Paper>
                <Paper elevation={0} sx={{padding: 2, border: '1px solid #e0e0e0', borderRadius: 2}}>
                    <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                        Total
                    </Typography>
                    <Typography variant="h6">{formatDuration(meta.totalDownloadAndRating)}</Typography>
                </Paper>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
                gap: 2
            }}>
                <Paper elevation={0} sx={{padding: 2.5, border: '1px solid #e0e0e0', borderRadius: 2}}>
                    <Typography variant="subtitle2" sx={{marginBottom: 1}}>
                        Function Calls
                    </Typography>
                    {fnsListEntries.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No function telemetry recorded.
                        </Typography>
                    ) : (
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                            {fnsListEntries
                                .sort((a, b) => b[1] - a[1])
                                .map(([name, count]) => (
                                    <Box key={name} sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
                                        <Chip
                                            label={formatNumber(count)}
                                            size="small"
                                            variant="filled"
                                            sx={{minWidth: 60, justifyContent: 'center'}}
                                        />
                                        <Typography variant="body2" sx={{fontFamily: 'monospace', color: 'text.secondary'}}>
                                            {name}
                                        </Typography>
                                    </Box>
                                ))}
                        </Box>
                    )}
                </Paper>

                <Paper elevation={0} sx={{padding: 2.5, border: '1px solid #e0e0e0', borderRadius: 2}}>
                    <Typography variant="subtitle2" sx={{marginBottom: 1}}>
                        Proposal Counts
                    </Typography>
                    {proposalsCountsEntries.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No proposal count data available.
                        </Typography>
                    ) : (
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                            {proposalsCountsEntries
                                .sort((a, b) => b[1] - a[1])
                                .map(([key, value]) => (
                                    <Box key={key} sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
                                        <Chip
                                            label={formatNumber(value)}
                                            size="small"
                                            sx={{minWidth: 60, justifyContent: 'center'}}
                                        />
                                        <Typography variant="body2" sx={{color: 'text.secondary'}}>
                                            {key}
                                        </Typography>
                                    </Box>
                                ))}
                        </Box>
                    )}
                </Paper>
            </Box>

            <Paper elevation={0} sx={{padding: 2.5, border: '1px solid #e0e0e0', borderRadius: 2}}>
                <Typography variant="subtitle2" sx={{marginBottom: 1}}>
                    Console Errors
                </Typography>
                <Divider sx={{marginBottom: 2}}/>
                {consoleErrors.length === 0 ? (
                    <Typography variant="body2" color="success.main">
                        No console errors recorded 🎉
                    </Typography>
                ) : (
                    <Box component="ul" sx={{margin: 0, paddingLeft: 3}}>
                        {consoleErrors.map((error: string, index: number) => (
                            <Typography component="li" variant="body2" key={`${error}-${index}`}>
                                {error}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Paper>
        </Box>
    )
}

export default DetailsProposalTab
